import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Cpu,
  RefreshCw,
  Upload,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Power,
  Settings,
  HardDrive
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const Modules = ({ connectionStatus }) => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showReprogramDialog, setShowReprogramDialog] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState("");
  const [reprogramConfirmation, setReprogramConfirmation] = useState("");
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [pyroFuseAcknowledged, setPyroFuseAcknowledged] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (connectionStatus.connected) {
      fetchModules();
    }
  }, [connectionStatus.connected]);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/modules`);
      setModules(response.data);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    }
  };

  const handleReset = async () => {
    if (resetConfirmation !== "RESET_MODULE_CONFIRM") {
      toast.error("Invalid confirmation code");
      return;
    }

    if (!safetyAcknowledged) {
      toast.error("You must acknowledge the safety warning");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/modules/reset`, {
        module_id: selectedModule.id,
        confirmation_code: resetConfirmation,
        safety_acknowledged: safetyAcknowledged
      });
      
      toast.success(response.data.message);
      setShowResetDialog(false);
      resetDialogState();
      fetchModules();
    } catch (error) {
      toast.error("Reset failed", {
        description: error.response?.data?.detail
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReprogram = async () => {
    if (reprogramConfirmation !== "REPROGRAM_MODULE_CONFIRM") {
      toast.error("Invalid confirmation code");
      return;
    }

    if (!safetyAcknowledged || !pyroFuseAcknowledged) {
      toast.error("You must acknowledge all safety warnings");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/modules/reprogram`, {
        module_id: selectedModule.id,
        firmware_data: "simulated_firmware_data",
        confirmation_code: reprogramConfirmation,
        safety_acknowledged: safetyAcknowledged,
        pyrotechnic_fuse_acknowledged: pyroFuseAcknowledged
      });
      
      toast.success(response.data.message, {
        description: `New firmware: ${response.data.new_firmware}`
      });
      setShowReprogramDialog(false);
      resetDialogState();
      fetchModules();
    } catch (error) {
      toast.error("Reprogram failed", {
        description: error.response?.data?.detail
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const resetDialogState = () => {
    setSelectedModule(null);
    setResetConfirmation("");
    setReprogramConfirmation("");
    setSafetyAcknowledged(false);
    setPyroFuseAcknowledged(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "online": return "text-[#30D158]";
      case "error": return "text-[#FF3B30]";
      default: return "text-white/50";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "online": return <CheckCircle className="h-5 w-5 text-[#30D158]" />;
      case "error": return <XCircle className="h-5 w-5 text-[#FF3B30]" />;
      default: return <Power className="h-5 w-5 text-white/50" />;
    }
  };

  const getModuleTypeIcon = (type) => {
    switch (type) {
      case "powertrain": return <Cpu />;
      case "charging": return <Power />;
      case "body": return <Settings />;
      case "network": return <HardDrive />;
      default: return <Cpu />;
    }
  };

  if (!connectionStatus.connected) {
    return (
      <div className="fade-in">
        <div className="header-bar">
          <div>
            <h1 className="header-title">Module Control</h1>
            <p className="header-subtitle">Reset and reprogram vehicle modules</p>
          </div>
        </div>
        
        <div className="bento-card text-center py-16">
          <Cpu className="h-16 w-16 text-white/20 mx-auto mb-4" />
          <p className="text-white/50 text-lg">No vehicle connected</p>
          <p className="text-white/30 text-sm mt-2">Connect to your Tesla to manage modules</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Module Control</h1>
          <p className="header-subtitle">Reset and reprogram vehicle modules</p>
        </div>
        
        <Button
          onClick={fetchModules}
          variant="outline"
          className="bg-transparent border-white/20 text-white hover:bg-white/5"
          data-testid="refresh-modules-btn"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Safety Warning Banner */}
      <div className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded service-mode">
        <div className="flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-[#FF3B30] mt-0.5" />
          <div>
            <p className="text-[#FF3B30] font-heading uppercase tracking-wider">Critical Safety Notice</p>
            <p className="text-white/60 text-sm mt-1">
              Resetting or reprogramming modules can affect vehicle safety systems. Ensure vehicle is in a safe location, 
              12V battery is properly charged, and you understand the implications of each action. Some operations cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* Module Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((module) => (
          <div 
            key={module.id}
            className={`module-card ${module.status}`}
            data-testid={`module-card-${module.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 glass-card rounded">
                  {getModuleTypeIcon(module.type)}
                </div>
                <div>
                  <h3 className="module-name">{module.name}</h3>
                  <p className="module-type">{module.type}</p>
                </div>
              </div>
              {getStatusIcon(module.status)}
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Firmware</span>
                <span className="font-mono text-[#007AFF]">{module.firmware_version}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Status</span>
                <span className={`font-mono uppercase ${getStatusColor(module.status)}`}>{module.status}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {module.can_reset && (
                <Button
                  onClick={() => {
                    setSelectedModule(module);
                    setShowResetDialog(true);
                  }}
                  variant="outline"
                  className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5 text-xs uppercase tracking-wider"
                  data-testid={`reset-btn-${module.id}`}
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Reset
                </Button>
              )}
              {module.can_reprogram && (
                <Button
                  onClick={() => {
                    setSelectedModule(module);
                    setShowReprogramDialog(true);
                  }}
                  className="flex-1 bg-[#FF9F0A]/20 border border-[#FF9F0A]/30 text-[#FF9F0A] hover:bg-[#FF9F0A]/30 text-xs uppercase tracking-wider"
                  data-testid={`reprogram-btn-${module.id}`}
                >
                  <Upload className="mr-1 h-3 w-3" />
                  Reprogram
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={(open) => {
        if (!open) resetDialogState();
        setShowResetDialog(open);
      }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider">
              Reset Module
            </DialogTitle>
            <DialogDescription className="text-white/60">
              You are about to reset: <span className="text-[#007AFF]">{selectedModule?.name}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#FF9F0A] mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#FF9F0A] font-medium">Warning</p>
                  <p className="text-white/60 mt-1">
                    Resetting this module will temporarily disable its functions. The vehicle may need to be restarted after the reset completes.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Checkbox
                id="safety-ack"
                checked={safetyAcknowledged}
                onCheckedChange={setSafetyAcknowledged}
                className="border-white/30 mt-1"
                data-testid="safety-acknowledge-checkbox"
              />
              <label htmlFor="safety-ack" className="text-sm text-white/80 cursor-pointer">
                I understand that resetting this module may affect vehicle operation and I have ensured the vehicle is in a safe location.
              </label>
            </div>
            
            <div>
              <label className="text-sm text-white/60 mb-2 block">
                Type <span className="font-mono text-[#007AFF]">RESET_MODULE_CONFIRM</span> to confirm:
              </label>
              <input
                type="text"
                value={resetConfirmation}
                onChange={(e) => setResetConfirmation(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-mono focus:border-[#007AFF] focus:outline-none"
                placeholder="Enter confirmation code"
                data-testid="reset-confirmation-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResetDialog(false);
                resetDialogState();
              }}
              className="bg-transparent border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReset}
              disabled={resetConfirmation !== "RESET_MODULE_CONFIRM" || !safetyAcknowledged || isProcessing}
              className="btn-primary"
              data-testid="confirm-reset-btn"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reset Module
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprogram Dialog */}
      <Dialog open={showReprogramDialog} onOpenChange={(open) => {
        if (!open) resetDialogState();
        setShowReprogramDialog(open);
      }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF9F0A]">
              Reprogram Module
            </DialogTitle>
            <DialogDescription className="text-white/60">
              You are about to reprogram: <span className="text-[#007AFF]">{selectedModule?.name}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            {/* Critical Warning */}
            <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded warning-flash">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-[#FF3B30] font-medium">CRITICAL - READ CAREFULLY</p>
                  <p className="text-white/60 mt-1 text-xs">
                    Reprogramming ECU modules is critical. Interrupting can permanently damage the module.
                  </p>
                  <ul className="text-white/60 mt-1 list-disc list-inside space-y-0.5 text-xs">
                    <li>12V battery fully charged (33Ah in frunk)</li>
                    <li>Stable power connection</li>
                    <li>Do NOT disconnect during programming</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Pyrotechnic Fuse Warning */}
            <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-[#FF3B30] font-medium">PYROTECHNIC FUSE NOTICE</p>
                  <p className="text-white/60 mt-1 text-xs">
                    2012-2013 Model S pyrotechnic fuse is between car floor and battery pack. Extremely difficult to access. HV issues may require professional service.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="safety-ack-reprogram"
                  checked={safetyAcknowledged}
                  onCheckedChange={setSafetyAcknowledged}
                  className="border-white/30 mt-1"
                  data-testid="safety-acknowledge-reprogram"
                />
                <label htmlFor="safety-ack-reprogram" className="text-xs text-white/80 cursor-pointer">
                  I understand this is critical and have ensured stable power and connections.
                </label>
              </div>
              
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="pyro-ack"
                  checked={pyroFuseAcknowledged}
                  onCheckedChange={setPyroFuseAcknowledged}
                  className="border-white/30 mt-1"
                  data-testid="pyro-fuse-acknowledge"
                />
                <label htmlFor="pyro-ack" className="text-xs text-white/80 cursor-pointer">
                  I acknowledge pyrotechnic fuse location and HV risks.
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-white/60 mb-2 block">
                Type <span className="font-mono text-[#FF9F0A]">REPROGRAM_MODULE_CONFIRM</span> to confirm:
              </label>
              <input
                type="text"
                value={reprogramConfirmation}
                onChange={(e) => setReprogramConfirmation(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-4 py-2 text-white font-mono text-sm focus:border-[#FF9F0A] focus:outline-none"
                placeholder="Enter confirmation code"
                data-testid="reprogram-confirmation-input"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t border-white/10">
            <Button
              variant="outline"
              onClick={() => {
                setShowReprogramDialog(false);
                resetDialogState();
              }}
              className="bg-transparent border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReprogram}
              disabled={
                reprogramConfirmation !== "REPROGRAM_MODULE_CONFIRM" || 
                !safetyAcknowledged || 
                !pyroFuseAcknowledged || 
                isProcessing
              }
              className="btn-destructive"
              data-testid="confirm-reprogram-btn"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Programming...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Reprogram Module
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Modules;
